import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from './LanguageContext';
import { personalityTypes } from '../data/questions';
import html2canvas from 'html2canvas';
import './ResultsScreen.css';

const ResultsScreen = ({ personalityType, scores, onRestart }) => {
  const { t, language } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const cardRef = useRef(null);
  
  const typeInfo = personalityTypes[personalityType];
  const isRTL = language === 'ur';

  const generateShareableCard = async () => {
    if (!cardRef.current) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: typeInfo.color,
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `MBTI-${personalityType}-${Date.now()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error generating card:', error);
    }
    setIsGenerating(false);
  };

  const shareResults = async () => {
    const shareData = {
      title: `My MBTI Personality Type: ${personalityType}`,
      text: `I just discovered I'm a ${typeInfo[language].name} (${personalityType})! Take the quiz to discover yours.`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      const text = `${shareData.text}\n${shareData.url}`;
      await navigator.clipboard.writeText(text);
      alert('Results copied to clipboard!');
    }
  };

  return (
    <div className="results-container">
      {/* Header */}
      <div className="results-header">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="results-title"
        >
          {t('results.yourType')}
        </motion.div>
      </div>

      {/* Result Card */}
      <motion.div
        ref={cardRef}
        className="result-card"
        style={{ backgroundColor: typeInfo.color }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="card-content" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Personality Type */}
          <motion.div
            className="personality-type"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="type-letters">{personalityType}</div>
            <div className="type-name">{typeInfo[language].name}</div>
          </motion.div>

          {/* Description */}
          <motion.div
            className="type-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {typeInfo[language].description}
          </motion.div>

          {/* Traits */}
          <motion.div
            className="traits-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3>{t('results.traits')}</h3>
            <div className="traits-grid">
              {typeInfo[language].traits.map((trait, index) => (
                <span key={index} className="trait-tag">
                  {trait}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Strengths */}
          <motion.div
            className="strengths-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3>{t('results.strengths')}</h3>
            <ul className="strengths-list">
              {typeInfo[language].strengths.map((strength, index) => (
                <li key={index}>{strength}</li>
              ))}
            </ul>
          </motion.div>

          {/* Famous People */}
          <motion.div
            className="famous-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3>{t('results.famousPeople')}</h3>
            <div className="famous-people">
              {typeInfo[language].famous.map((person, index) => (
                <span key={index} className="famous-person">
                  {person}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Scores */}
          <motion.div
            className="scores-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h3>Personality Breakdown</h3>
            <div className="scores-grid">
              <div className="score-item">
                <span className="score-label">Extraversion</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.E / (scores.E + scores.I)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.E}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Introversion</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.I / (scores.E + scores.I)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.I}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Sensing</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.S / (scores.S + scores.N)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.S}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Intuition</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.N / (scores.S + scores.N)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.N}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Thinking</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.T / (scores.T + scores.F)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.T}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Feeling</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.F / (scores.T + scores.F)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.F}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Judging</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.J / (scores.J + scores.P)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.J}</span>
              </div>
              <div className="score-item">
                <span className="score-label">Perceiving</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${(scores.P / (scores.J + scores.P)) * 100}%` }}
                  />
                </div>
                <span className="score-value">{scores.P}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        className="action-buttons"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <button 
          className="action-btn primary"
          onClick={generateShareableCard}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating...' : t('results.downloadCard')}
        </button>
        
        <button 
          className="action-btn secondary"
          onClick={shareResults}
        >
          {t('results.shareResults')}
        </button>
        
        <button 
          className="action-btn tertiary"
          onClick={onRestart}
        >
          {t('results.takeAgain')}
        </button>
      </motion.div>
    </div>
  );
};

export default ResultsScreen;
